import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { ScreenHeader, Surface, Button, IconButton, Badge, Avatar, Divider, Icon } from "../components/ui";
import CropFotoModal from "../components/CropFotoModal";

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(iso) {
  if (!iso || iso.length !== 10) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function ProfileField({ label, value, icon, mono = false, secret = false, onEdit }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
      padding: 'var(--pad-card)', minHeight: 48,
    }}>
      {icon && (
        <div style={{
          width: 28, height: 28, borderRadius: 7, background: 'var(--bg-sunken)',
          color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={icon} size={13}/>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>{label}</div>
        <div style={{
          fontSize: 13.5, color: 'var(--text-primary)',
          fontFamily: mono ? '"Geist Mono", ui-monospace, monospace' : 'inherit',
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
          letterSpacing: mono ? 0 : -0.1,
          whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden',
        }}>{value || '—'}</div>
      </div>
      {onEdit && (
        <IconButton icon={secret ? 'edit' : 'chevron'} size={28} variant="ghost" onClick={onEdit}/>
      )}
    </div>
  );
}

function EditModal({ perfil, onClose, onSave }) {
  const [form, setForm] = useState({
    nome: perfil?.nome || '',
    email: perfil?.email || '',
    telefone: perfil?.telefone || '',
    endereco: perfil?.endereco || '',
    data_nascimento: perfil?.data_nascimento || '',
    senha: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = { ...form };
    if (!payload.senha) delete payload.senha;
    axios.put(`${API_URL}/perfil`, payload, { withCredentials: true })
      .then(() => onSave())
      .catch(err => setError(err.response?.data?.erro || 'Falha ao salvar.'))
      .finally(() => setSaving(false));
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
        boxShadow: 'var(--shadow-lg)',
        animation: 'fadeSlideUp .25s cubic-bezier(.22,1,.36,1)',
        maxHeight: '90vh', overflow: 'auto',
      }} className="mema-scroll">
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--gap-3)',
          borderBottom: '1px solid var(--border-hairline)',
          position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.3 }}>Editar perfil</div>
          <IconButton icon="close" onClick={onClose} variant="ghost"/>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 'var(--gap-3)', display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)' }}>
          {[
            { name: 'nome', label: 'Nome completo', type: 'text', required: true },
            { name: 'email', label: 'E-mail', type: 'email', required: true },
            { name: 'telefone', label: 'Telefone', type: 'tel' },
            { name: 'endereco', label: 'Endereço', type: 'text' },
            { name: 'data_nascimento', label: 'Data de nascimento', type: 'date' },
            { name: 'senha', label: 'Nova senha (deixe em branco para manter)', type: 'password', placeholder: '••••••••' },
          ].map(f => (
            <div key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>{f.label}</label>
              <input
                name={f.name}
                type={f.type}
                value={form[f.name]}
                onChange={handleChange}
                required={f.required}
                placeholder={f.placeholder}
                autoComplete={f.name === 'senha' ? 'new-password' : undefined}
                style={{
                  height: 40, padding: '0 12px', fontSize: 16,
                  background: 'var(--bg-sunken)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
            </div>
          ))}

          {error && (
            <div style={{ fontSize: 13, color: 'var(--danger)', padding: '8px 12px', background: 'color-mix(in oklch, var(--danger) 10%, transparent)', borderRadius: 'var(--radius-sm)' }}>
              {error}
            </div>
          )}

          <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function MeuPerfil() {
  const { tipo, logout } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [cropSrc, setCropSrc] = useState(null);
  const fileInputRef = useRef();

  function load() {
    setLoading(true);
    axios.get(`${API_URL}/perfil`, { withCredentials: true })
      .then(r => setPerfil(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  function handleFotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so the same file can be re-selected if cancelled
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  }

  function uploadBlob(blob) {
    const formData = new FormData();
    formData.append('arquivo', blob, 'foto.jpg');
    axios.post(`${API_URL}/upload_foto`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(r => { setPerfil(p => ({ ...p, foto_url: r.data.foto_url })); setMsg('Foto atualizada!'); })
      .catch(() => setMsg('Falha ao enviar imagem.'))
      .finally(() => setCropSrc(null));
  }

  if (loading && !perfil) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Carregando…</div>
      </div>
    );
  }

  const avatarSrc = perfil?.foto_url ? `${API_URL}${perfil.foto_url}` : null;
  const isAluno = tipo === 'aluno';

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Meu perfil" action={
        <Button size="sm" variant="secondary" icon="edit" onClick={() => setEditOpen(true)}>Editar</Button>
      }/>

      {/* Identity */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-3)', paddingTop: 8 }}>
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}>
            {avatarSrc
              ? <img src={avatarSrc} alt="Foto" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover',
                  boxShadow: '0 0 0 2px var(--bg-surface), 0 0 0 3.5px var(--accent)' }}/>
              : <Avatar name={perfil?.nome || '?'} size={64} ring/>
            }
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 22, height: 22, borderRadius: '50%',
              background: 'var(--accent)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid var(--bg-surface)',
            }}>
              <Icon name="cam" size={11}/>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoChange}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.5, lineHeight: 1.15, color: 'var(--text-primary)' }}>
              {perfil?.nome || '—'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <Badge tone="accent">{isAluno ? 'Aluno' : 'Professora'}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback message */}
      {msg && (
        <div style={{ margin: '0 var(--gap-3)', padding: '10px 14px', borderRadius: 'var(--radius-md)', fontSize: 13,
          background: msg.includes('Falha') ? 'color-mix(in oklch, var(--danger) 10%, transparent)' : 'color-mix(in oklch, var(--success) 10%, transparent)',
          color: msg.includes('Falha') ? 'var(--danger)' : 'var(--success)',
        }}>
          {msg}
        </div>
      )}

      {/* Personal section */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <h2 style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '0 0 var(--gap-2)' }}>Pessoal</h2>
        <Surface>
          <ProfileField label="Nome" value={perfil?.nome}/>
          <Divider/>
          <ProfileField label="Data de nascimento" value={formatDate(perfil?.data_nascimento)} mono/>
        </Surface>
      </div>

      {/* Contact section */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <h2 style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '0 0 var(--gap-2)' }}>Contato</h2>
        <Surface>
          <ProfileField label="E-mail" value={perfil?.email} icon="mail"/>
          <Divider/>
          <ProfileField label="Telefone" value={perfil?.telefone} icon="phone" mono/>
          <Divider/>
          <ProfileField label="Endereço" value={perfil?.endereco} icon="location"/>
        </Surface>
      </div>

      {/* Account section */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <h2 style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '0 0 var(--gap-2)' }}>Conta</h2>
        <Surface>
          <ProfileField label="Login" value={perfil?.login} mono/>
          <Divider/>
          <ProfileField label="Senha" value="••••••••" secret onEdit={() => setEditOpen(true)}/>
        </Surface>
      </div>

      {/* Logout */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <button onClick={logout} style={{
          width: '100%', height: 40,
          background: 'transparent', color: 'var(--danger)',
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 540, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <Icon name="logout" size={15}/>
          Sair da conta
        </button>
      </div>

      {cropSrc && (
        <CropFotoModal
          imageSrc={cropSrc}
          onCancel={() => setCropSrc(null)}
          onSave={uploadBlob}
        />
      )}

      {editOpen && (
        <EditModal
          perfil={perfil}
          onClose={() => setEditOpen(false)}
          onSave={() => { setEditOpen(false); setMsg('Perfil atualizado!'); load(); }}
        />
      )}
    </div>
  );
}
